import { useEffect, useMemo, useState, useRef } from 'react';
import { Container } from '@/components/container';
import { DataGrid } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetChurnReportsQuery,
  useLazyGetChurnReportsCsvQuery,
} from '@/services/ChurnReportsService';
import { GroupByUnit, SubscriptionType } from '@/constant';
import { useSnackbar } from 'notistack';
import Select, { MultiValue } from 'react-select';
import ApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { ChurnFilters } from '@/types/churn-reports.types';
import { generateReportPdf } from '@/utils/pdfExport';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import { formatDisplayDate, formatDisplayDateRange } from '@/utils/Date';
const numberFormat = (n: number) => new Intl.NumberFormat().format(n);
const stripHtml = (input: string) => input.replace(/<[^>]*>/g, '').trim();

type SelectOption = { value: string; label: string };

const CancellationReports = () => {
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const chartRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const groupByOptions: SelectOption[] = [
    { value: GroupByUnit.DAY, label: 'Daily' },
    { value: GroupByUnit.WEEK, label: 'Weekly' },
    { value: GroupByUnit.MONTH, label: 'Monthly' },
  ];
  const subscriptionTypeOptions: SelectOption[] = [
    { value: String(SubscriptionType.BOATER), label: 'Boater' },
    { value: String(SubscriptionType.HARBOR), label: 'Harbor' },
    { value: String(SubscriptionType.CLUB), label: 'Club' },
    { value: String(SubscriptionType.DMO), label: 'DMO' },
    {
      value: String(SubscriptionType.SERVICE_PROVIDER),
      label: 'Service Provider',
    },
  ];

  const [selectedGroupBy, setSelectedGroupBy] = useState<SelectOption | null>(
    groupByOptions[2]
  );
  const [selectedSubscriptionTypes, setSelectedSubscriptionTypes] = useState<
    SelectOption[]
  >([]);
  const [subscriptionOptions, setSubscriptionOptions] = useState<
    SelectOption[]
  >([]);
  const [selectedSubs, setSelectedSubs] = useState<SelectOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(25);
  const [getCsv, { isLoading: csvLoading }] = useLazyGetChurnReportsCsvQuery();
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  const handleExportPdf = async () => {
    try {
      setPdfLoading(true);
      const date = new Date().toISOString().slice(0, 10);

      await generateReportPdf({
        filename: `cancellation_reports_${date}.pdf`,
        title: 'Cancellation Reports',
        subtitle: `Grouped by ${selectedGroupBy?.label || 'Month'}`,
        chartElement: chartRef.current || undefined,
        tableElement: tableRef.current || undefined,
        orientation: 'landscape',
        tableData: (() => {
          // Build full table rows from current data (not just visible DOM)
          const map: Record<
            string,
            {
              period: string;
              period_range?: string;
              expired: number;
              manual: number;
              downgrades: number;
              upgrades: number;
            }
          > = {};
          (data?.expired || []).forEach((e) => {
            const key = e.period;
            map[key] = map[key] || {
              period: key,
              period_range: e.period_range,
              expired: 0,
              manual: 0,
              downgrades: 0,
              upgrades: 0,
            };
            if (!map[key].period_range && e.period_range)
              map[key].period_range = e.period_range;
            map[key].expired += e.cancellations || 0;
          });
          (data?.manualCancels || []).forEach((m) => {
            const key = m.period;
            map[key] = map[key] || {
              period: key,
              period_range: m.period_range,
              expired: 0,
              manual: 0,
              downgrades: 0,
              upgrades: 0,
            };
            if (!map[key].period_range && m.period_range)
              map[key].period_range = m.period_range;
            map[key].manual += m.cancellations || 0;
          });
          (data?.downgrades || []).forEach((d) => {
            const key = d.period;
            map[key] = map[key] || {
              period: key,
              period_range: d.period_range,
              expired: 0,
              manual: 0,
              downgrades: 0,
              upgrades: 0,
            };
            if (!map[key].period_range && d.period_range)
              map[key].period_range = d.period_range;
            map[key].downgrades += d.downgrades || 0;
          });
          (data?.upgrades || []).forEach((u) => {
            const key = u.period;
            map[key] = map[key] || {
              period: key,
              period_range: u.period_range,
              expired: 0,
              manual: 0,
              downgrades: 0,
              upgrades: 0,
            };
            if (!map[key].period_range && u.period_range)
              map[key].period_range = u.period_range;
            map[key].upgrades += u.upgrades || 0;
          });
          const sorted = Object.values(map).sort(
            (a, b) =>
              new Date(a.period).getTime() - new Date(b.period).getTime()
          );
          return {
            headers: [
              'Period',
              'Expired',
              'Manual Cancels',
              'Downgrades',
              'Upgrades',
              'Total Cancellations',
            ],
            rows: sorted.map((r) => [
              r.period_range
                ? formatDisplayDateRange(r.period_range)
                : formatDisplayDate(new Date(r.period)),
              numberFormat(r.expired),
              numberFormat(r.manual),
              numberFormat(r.downgrades),
              numberFormat(r.upgrades),
              numberFormat(r.expired + r.manual),
            ]),
          };
        })(),
      });

      enqueueSnackbar('PDF exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const filters: ChurnFilters = useMemo(() => {
    // Determine from/to defaults:
    // - Day/Week: last 30 days inclusive (today and previous 29 days)
    // - Year: start and end of current year
    // - Month: current date to 365 days ago
    const now = new Date();
    const formatDate = (d: Date) => d.toISOString().slice(0, 10);

    let from: string | undefined;
    let to: string | undefined;

    const grp = selectedGroupBy?.value;
    const isDayOrWeek = grp === GroupByUnit.DAY || grp === GroupByUnit.WEEK;
    const isYear = grp === ('year' as unknown as GroupByUnit);
    const isMonth = grp === GroupByUnit.MONTH;

    const hasCustomStart =
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid();
    const hasCustomEnd =
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid();

    if (isDayOrWeek) {
      // Default to rolling last 30 days
      const defaultFrom = moment().subtract(29, 'days').format('YYYY-MM-DD');
      const defaultTo = formatDate(now);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else if (isYear) {
      // Default to current year bounds
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const endOfYear = new Date(now.getFullYear(), 11, 31);
      const defaultFrom = formatDate(startOfYear);
      const defaultTo = formatDate(endOfYear);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else if (isMonth) {
      // Month grouping: default to current date to 365 days ago
      const defaultFrom = moment().subtract(365, 'days').format('YYYY-MM-DD');
      const defaultTo = formatDate(now);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else {
      // Other groupings: only send if user picked a custom range
      if (hasCustomStart && hasCustomEnd) {
        from = moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        to = moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      }
    }

    return {
      groupBy: selectedGroupBy?.value,
      subscription_types: selectedSubscriptionTypes.map((o) => Number(o.value)),
      subscription_ids: selectedSubs.length
        ? selectedSubs.map((o) => Number(o.value))
        : undefined,
      from,
      to,
      page: currentPage,
      limit: pageLimit,
    } as ChurnFilters;
  }, [
    selectedGroupBy,
    selectedSubscriptionTypes,
    selectedSubs,
    tempStartDate,
    tempEndDate,
    currentPage,
    pageLimit,
  ]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSelectedGroupBy(groupByOptions[2]); // Month
    setSelectedSubscriptionTypes([]);
    setSelectedSubs([]);
    setTempStartDate('');
    setTempEndDate('');
    setCurrentPage(1);
  };

  const { data, isLoading, isFetching, error, isError } =
    useGetChurnReportsQuery(filters);

  // Load subscriptions for user-friendly selection
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await SubscriptionsService.getSubscriptions(1, 100);
      if (!mounted) return;
      const opts: SelectOption[] = (res.subscriptions || []).map((s) => ({
        value: String(s.id),
        label: stripHtml(s.name || ''),
      }));
      setSubscriptionOptions(opts);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (isError && !isLoading && !isFetching && !data) {
      const err = error as unknown as { data?: unknown; error?: string };
      let message = 'Failed to fetch cancellation reports';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [isError, error, isLoading, isFetching, data, enqueueSnackbar]);

  const chart = useMemo(() => {
    const periodsSet = new Set<string>();
    (data?.expired || []).forEach((e) => periodsSet.add(e.period));
    (data?.manualCancels || []).forEach((m) => periodsSet.add(m.period));
    (data?.downgrades || []).forEach((d) => periodsSet.add(d.period));
    (data?.upgrades || []).forEach((u) => periodsSet.add(u.period));
    const periods = Array.from(periodsSet).sort();
    const categories = periods.map((p) => formatDisplayDate(new Date(p)));

    const expiredSeries = periods.map((p) =>
      (data?.expired || [])
        .filter((e) => e.period === p)
        .reduce((s, e) => s + (e.cancellations || 0), 0)
    );
    const manualCancelsSeries = periods.map((p) =>
      (data?.manualCancels || [])
        .filter((m) => m.period === p)
        .reduce((s, m) => s + (m.cancellations || 0), 0)
    );
    const downgradesSeries = periods.map((p) =>
      (data?.downgrades || [])
        .filter((d) => d.period === p)
        .reduce((s, d) => s + (d.downgrades || 0), 0)
    );
    const upgradesSeries = periods.map((p) =>
      (data?.upgrades || [])
        .filter((u) => u.period === p)
        .reduce((s, u) => s + (u.upgrades || 0), 0)
    );

    const series = [
      { name: 'Expired (Non-Renewal)', data: expiredSeries },
      { name: 'Manual Cancellations', data: manualCancelsSeries },
      { name: 'Downgrades', data: downgradesSeries },
      { name: 'Upgrades', data: upgradesSeries },
    ];

    const options: ApexOptions = {
      chart: { type: 'area', height: 300, toolbar: { show: false } },
      dataLabels: { enabled: false },
      legend: { show: true },
      stroke: { curve: 'smooth', show: true, width: 3 },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: 'var(--tw-gray-500)', fontSize: '12px' } },
      },
      yaxis: {
        labels: {
          formatter: (val) => Number(val).toFixed(2),
          style: { colors: 'var(--tw-gray-500)', fontSize: '12px' },
        },
      },
      tooltip: { y: { formatter: (val) => Number(val).toFixed(2) } },
      fill: { gradient: { opacityFrom: 0.25, opacityTo: 0 } },
      grid: {
        borderColor: 'var(--tw-gray-200)',
        strokeDashArray: 5,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
    };

    return { options, series };
  }, [data]);

  const handleExportCsv = async () => {
    try {
      const blob = await getCsv({ ...filters, isCsv: true }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `cancellation_reports_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('CSV exported successfully', { variant: 'success' });
    } catch (e) {
      let message = 'Failed to export CSV';
      if (
        e &&
        typeof e === 'object' &&
        'data' in (e as Record<string, unknown>)
      ) {
        const data = (e as { data?: { message?: string } }).data;
        if (data?.message) message = data.message;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  // Prepare a flattened table by period with totals
  // Note: The API returns paginated results for each array (expired, manualCancels, downgrades, upgrades)
  // We merge them by period and then apply pagination on the merged results
  const { tableRows, totalMergedItems } = useMemo(() => {
    const map: Record<
      string,
      {
        period: string;
        period_range?: string;
        expired: number;
        manual: number;
        downgrades: number;
        upgrades: number;
      }
    > = {};
    (data?.expired || []).forEach((e) => {
      const key = e.period;
      map[key] = map[key] || {
        period: key,
        period_range: e.period_range,
        expired: 0,
        manual: 0,
        downgrades: 0,
        upgrades: 0,
      };
      if (!map[key].period_range && e.period_range)
        map[key].period_range = e.period_range;
      map[key].expired += e.cancellations || 0;
    });
    (data?.manualCancels || []).forEach((m) => {
      const key = m.period;
      map[key] = map[key] || {
        period: key,
        period_range: m.period_range,
        expired: 0,
        manual: 0,
        downgrades: 0,
        upgrades: 0,
      };
      if (!map[key].period_range && m.period_range)
        map[key].period_range = m.period_range;
      map[key].manual += m.cancellations || 0;
    });
    (data?.downgrades || []).forEach((d) => {
      const key = d.period;
      map[key] = map[key] || {
        period: key,
        period_range: d.period_range,
        expired: 0,
        manual: 0,
        downgrades: 0,
        upgrades: 0,
      };
      if (!map[key].period_range && d.period_range)
        map[key].period_range = d.period_range;
      map[key].downgrades += d.downgrades || 0;
    });
    (data?.upgrades || []).forEach((u) => {
      const key = u.period;
      map[key] = map[key] || {
        period: key,
        period_range: u.period_range,
        expired: 0,
        manual: 0,
        downgrades: 0,
        upgrades: 0,
      };
      if (!map[key].period_range && u.period_range)
        map[key].period_range = u.period_range;
      map[key].upgrades += u.upgrades || 0;
    });

    // Sort by period and apply pagination on the merged results
    const sorted = Object.values(map).sort(
      (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
    );

    // Store total count of unique merged periods
    const total = sorted.length;

    // Apply pagination on merged results
    // Calculate start and end indices based on current page and limit
    const startIndex = (currentPage - 1) * pageLimit;
    const endIndex = startIndex + pageLimit;
    const paginatedRows = sorted.slice(startIndex, endIndex);

    return {
      tableRows: paginatedRows.map((r) => ({
        ...r,
        total_cancellations: r.expired + r.manual,
      })),
      totalMergedItems: total,
    };
  }, [data, currentPage, pageLimit]);

  const gridColumns = [
    {
      id: 'period',
      header: 'Period',
      accessorFn: (row: { period: string; period_range?: string }) =>
        row.period_range
          ? formatDisplayDateRange(row.period_range)
          : formatDisplayDate(new Date(row.period)),
      enableSorting: false,
    },
    {
      id: 'expired',
      header: 'Expired',
      accessorFn: (row: { expired: number }) => numberFormat(row.expired),
      enableSorting: false,
    },
    {
      id: 'manual',
      header: 'Manual Cancels',
      accessorFn: (row: { manual: number }) => numberFormat(row.manual),
      enableSorting: false,
    },
    {
      id: 'downgrades',
      header: 'Downgrades',
      accessorFn: (row: { downgrades: number }) => numberFormat(row.downgrades),
      enableSorting: false,
    },
    {
      id: 'upgrades',
      header: 'Upgrades',
      accessorFn: (row: { upgrades: number }) => numberFormat(row.upgrades),
      enableSorting: false,
    },
    {
      id: 'total_cancellations',
      header: 'Total Cancellations',
      accessorFn: (row: { total_cancellations: number }) =>
        numberFormat(row.total_cancellations),
      enableSorting: false,
    },
  ];

  return (
    <Container className="cancellation-reports">
      {hasPermission('subscription_management_read') && (
        <>
          <div className="flex items-start justify-between mb-6">
            <h1
              id="cancellation-reports-heading"
              className="text-xl font-semibold"
              tabIndex={-1}
            >
              Cancellation Reports
            </h1>
            <div className="flex flex-wrap gap-3 sm:gap-5 justify-start sm:justify-end">
              <div className="min-w-[160px]">
                <Select
                  classNamePrefix="react-select"
                  placeholder="Group by"
                  value={selectedGroupBy}
                  options={groupByOptions}
                  onChange={(val) => {
                    setSelectedGroupBy(val as SelectOption);
                    setCurrentPage(1);
                  }}
                  aria-label="Group by"
                />
              </div>
              <div className="min-w-[220px]">
                <Select
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="Customer Segment"
                  options={subscriptionTypeOptions}
                  value={selectedSubscriptionTypes}
                  onChange={(vals: MultiValue<SelectOption>) =>
                    setSelectedSubscriptionTypes(vals as SelectOption[])
                  }
                  aria-label="Customer Segment"
                />
              </div>
              <div className="min-w-[240px]">
                <Select
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="Filter subscriptions"
                  options={subscriptionOptions}
                  value={selectedSubs}
                  onChange={(vals: MultiValue<SelectOption>) =>
                    setSelectedSubs(vals as SelectOption[])
                  }
                  aria-label="Filter subscriptions"
                />
              </div>
              <div className="flex-shrink-0">
                <DateRangePicker
                  startDateName="from"
                  endDateName="to"
                  isMandatory={false}
                  value={[tempStartDate, tempEndDate]}
                  onChange={([newStartDate, newEndDate]) => {
                    handleDateRangeChange(
                      newStartDate as string,
                      newEndDate as string
                    );
                  }}
                  allowPastDates={true}
                  allowFutureDates={false}
                  className="w-full sm:w-64 h-8 sm:h-10 text-sm"
                />
              </div>
              {(selectedGroupBy?.value !== GroupByUnit.MONTH ||
                selectedSubscriptionTypes.length > 0 ||
                selectedSubs.length > 0 ||
                !!tempStartDate ||
                !!tempEndDate) && (
                <div className="flex items-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-light flex items-center gap-2"
                    onClick={resetFilters}
                    title="Clear filters"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleExportPdf}
                  disabled={pdfLoading}
                >
                  {pdfLoading ? 'Exporting...' : 'Export PDF'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleExportCsv}
                  disabled={csvLoading}
                >
                  {csvLoading ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <div
              className="card-body flex flex-col justify-end items-stretch grow px-3 py-1"
              ref={chartRef}
            >
              <ApexChart
                id="cancellation_reports_chart"
                options={chart.options}
                series={chart.series as ApexOptions['series']}
                type="area"
                width="100%"
                height="300"
              />
            </div>
          </div>

          <div className="card">
            <div className="card-body" ref={tableRef}>
              <DataGrid
                columns={gridColumns}
                data={tableRows}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: currentPage - 1,
                  size: pageLimit,
                  more: currentPage < Math.ceil(totalMergedItems / pageLimit),
                  totalItems: totalMergedItems,
                  totalPages: Math.ceil(totalMergedItems / pageLimit),
                  setPagination: (updater) => {
                    const newState =
                      typeof updater === 'function'
                        ? updater({
                            pageIndex: currentPage - 1,
                            pageSize: pageLimit,
                          })
                        : updater;
                    setCurrentPage(newState.pageIndex + 1);
                    setPageLimit(newState.pageSize);
                  },
                  info: '{from} - {to} of {count}',
                  sizes: [10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                serverSide={true}
                onFilterReset={resetFilters}
                hasActiveFilters={
                  selectedGroupBy?.value !== GroupByUnit.MONTH ||
                  selectedSubscriptionTypes.length > 0 ||
                  selectedSubs.length > 0 ||
                  !!tempStartDate ||
                  !!tempEndDate
                }
                aria-label="Cancellation reports table"
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default CancellationReports;
